import React, { useState, useCallback } from 'react';

// Type for input/textarea element refs since InputComponent was removed
type InputElementRef = React.RefObject<HTMLInputElement | HTMLTextAreaElement>;

export interface CaretPosition {
  start: number;
  end: number;
}

export interface Coordinates {
  x: number;
  y: number;
  maxHeight: number;
  placement: 'above' | 'below';
}

/**
 * Custom hook for tracking and managing caret position in input elements
 */
export function useCaretPosition(inputRef: InputElementRef) {
  const [caretPosition, setCaretPosition] = useState<CaretPosition>({
    start: 0,
    end: 0
  });

  const updateCaretPosition = useCallback(() => {
    if (inputRef.current) {
      const start = inputRef.current.selectionStart || 0;
      const end = inputRef.current.selectionEnd || 0;
      setCaretPosition({ start, end });
    }
  }, [inputRef]);

  const setCaretAt = useCallback(
    (position: number) => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(position, position);
        inputRef.current.focus();
        setCaretPosition({ start: position, end: position });
      }
    },
    [inputRef]
  );

  const getCaretCoordinates = useCallback(
    (customPosition?: number): Coordinates => {
      if (!inputRef.current)
        return { x: 0, y: 0, maxHeight: 300, placement: 'below' };

      const input = inputRef.current;
      const rect = input.getBoundingClientRect();
      const style = window.getComputedStyle(input);

      // Use custom position or current caret position
      const caretPos =
        customPosition !== undefined
          ? customPosition
          : input.selectionStart || 0;

      let x = rect.left;
      let y = rect.bottom; // Start with bottom of input as default

      // Handle textarea with proper multiline support
      if (input.tagName.toLowerCase() === 'textarea') {
        // Create accurate measurement container that matches textarea exactly
        const measureDiv = document.createElement('div');
        measureDiv.style.cssText = `
        position: absolute;
        visibility: hidden;
        left: -9999px;
        top: -9999px;
        height: auto;
        width: ${rect.width}px;
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow-wrap: break-word;
        border: ${style.border};
        padding: ${style.padding};
        margin: ${style.margin};
        font: ${style.font};
        line-height: ${style.lineHeight};
        letter-spacing: ${style.letterSpacing};
        text-align: ${style.textAlign};
        text-indent: ${style.textIndent};
        text-transform: ${style.textTransform};
        word-spacing: ${style.wordSpacing};
        box-sizing: ${style.boxSizing};
      `;

        const textBeforeCaret = input.value.substring(0, caretPos);
        measureDiv.textContent = textBeforeCaret;

        // Create caret marker
        const caretSpan = document.createElement('span');
        caretSpan.textContent = '.';
        measureDiv.appendChild(caretSpan);

        document.body.appendChild(measureDiv);

        const caretSpanRect = caretSpan.getBoundingClientRect();
        const measureDivRect = measureDiv.getBoundingClientRect();

        // Calculate accurate position accounting for all CSS properties
        const paddingLeft = parseInt(style.paddingLeft) || 0;
        const paddingTop = parseInt(style.paddingTop) || 0;
        const borderLeftWidth = parseInt(style.borderLeftWidth) || 0;
        const borderTopWidth = parseInt(style.borderTopWidth) || 0;

        x =
          rect.left +
          borderLeftWidth +
          // paddingLeft +
          (caretSpanRect.left - measureDivRect.left) -
          8; // Move 8px more to the left
        y =
          rect.top +
          borderTopWidth +
          paddingTop +
          (caretSpanRect.top - measureDivRect.top); // Use top instead of bottom to avoid overlap

        document.body.removeChild(measureDiv);
      } else {
        // For single-line inputs, use canvas measurement for precision
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (context) {
          context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
          const textWidth = context.measureText(
            input.value.substring(0, caretPos)
          ).width;
          const paddingLeft = parseInt(style.paddingLeft) || 0;
          const borderLeftWidth = parseInt(style.borderLeftWidth) || 0;
          x = rect.left + borderLeftWidth + paddingLeft + textWidth - 8; // Move 8px to the left
          y = rect.top; // Use top of input to avoid overlap
        }
      }

      // Viewport calculations for smart placement
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const popupWidth = 240;
      const popupHeight = 300;
      const margin = 16;
      const gap = 10; // User requested 5px gap

      let maxHeight = popupHeight;
      let placement: 'above' | 'below' = 'below';
      let finalY = y;

      // Since we're positioning at text line level, adjust for proper spacing
      const lineHeight = parseInt(style.lineHeight) || 20;

      // Check for bottom overflow - popup should appear below the text line
      const belowPosition = y + lineHeight + gap;
      const bottomOverflow =
        belowPosition + popupHeight > viewportHeight - margin;

      if (bottomOverflow) {
        // Calculate space above the text line
        const abovePosition = y - gap;
        const spaceAbove = abovePosition - margin;

        if (spaceAbove >= 120) {
          // Enough space above - place popup above the text
          placement = 'above';
          finalY = abovePosition; // Will be adjusted by CSS transform for "above" placement
          maxHeight = Math.min(spaceAbove, popupHeight);
        } else {
          // Not enough space above - place below with limited height
          finalY = belowPosition;
          const availableSpaceBelow = viewportHeight - belowPosition - margin;
          maxHeight = Math.min(availableSpaceBelow, popupHeight);
        }
      } else {
        // Enough space below - place popup below the text line
        finalY = belowPosition;
      }

      // Prevent horizontal overflow
      let finalX = Math.max(
        margin,
        Math.min(x, viewportWidth - popupWidth - margin)
      );

      return {
        x: finalX,
        y: finalY,
        maxHeight,
        placement
      };
    },
    [inputRef]
  );

  return {
    caretPosition,
    updateCaretPosition,
    setCaretAt,
    getCaretCoordinates
  };
}
